import { useCallback, useMemo, useEffect, useState } from "react";
import ChecklistSidebar from "./ChecklistSidebar";
import InspectionPaginatedTable from "./InspectionPaginatedTable";
import AddTaskModal from "../../components/Modal/AddTaskModal";
import AddTaskLinkModal from "../../components/Modal/AddTaskLinkModal";
import { useInspections } from "../../Helpers/CustomHooks/useInspections";
import { useModalState } from "../../Helpers/CustomHooks/useModalState";
import { TaskFormData, TaskLinkFormData } from "../../Helpers/CustomHooks/inspections";
import { ModelApiItem } from "../../types/Model/ModelSchema";
import { TInspectionTask, TInspectionTaskLink } from "../../types/types";
import Loader from "../../components/CoreComponents/Loader";


// Shared button styles
const primaryButtonClass = "bg-orange-400 text-white px-4 py-2 rounded flex items-center hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed";

export default function InspectionsDetails() {
    // Store only the model number - the hook will look up fresh data from Redux
    const [selectedModelNumber, setSelectedModelNumber] = useState<string | null>(null);
    const [currentActiveInspectionTask, setCurrentActiveInspectionTask] = useState<{ original: TInspectionTask } | null>(null);
    const [inspectionSearchTerm, setInspectionSearchTerm] = useState("");
    const [linkSearchTerm, setLinkSearchTerm] = useState("");

    // Modal state using custom hook
    const taskModal = useModalState<TInspectionTask>();
    const taskLinkModal = useModalState<TInspectionTaskLink>();

    const onSelectModel = useCallback((model: ModelApiItem) => {
        setSelectedModelNumber(model.modelNumber);
        setCurrentActiveInspectionTask(null);
    }, []);

    const {
        inspectionTaskTableData,
        inspectionPagination,
        injectedModelData,
        inspectionTaskLinksData,
        inspectionTaskLinksPagination,
        addTask,
        editTask,
        addTaskLink,
        editTaskLink,
        onClickAddInspectionToModel,
        isLoading,
        newlyCreatedInspection,
        clearNewlyCreatedInspection,
        currentActiveModel,
        activeInspectionTaskId,
    } = useInspections({
        selectedModelNumber,
        inspectionSearchTerm,
        linkSearchTerm,
        currentActiveInspectionTask,
        setCurrentActiveInspectionTask,
        onEditTask: taskModal.handleOpenForEdit,
        onEditTaskLink: taskLinkModal.handleOpenForEdit
    });

    // When a new inspection is created, automatically open the Add Task modal and select the model
    useEffect(() => {
        if (newlyCreatedInspection) {
            setSelectedModelNumber(newlyCreatedInspection.modelNumber);
            clearNewlyCreatedInspection();
        }
    }, [newlyCreatedInspection, clearNewlyCreatedInspection, taskModal]);

    // Task modal submit handler - no need to close here, the modal handles its own close
    const handleTaskSubmit = useCallback(async (data: TaskFormData) => {
        if (taskModal.itemToEdit) {
            const newTaskId = await editTask(taskModal.itemToEdit.inspectionTaskId, data);
            // After edit, the backend creates a new task with a new ID - update the active selection
            if (newTaskId) {
                setCurrentActiveInspectionTask(prev => prev ? {
                    original: { ...prev.original, ...data, inspectionTaskId: newTaskId, listOrder: data.order }
                } : null);
            }
        } else {
            await addTask(data);
        }
        taskModal.handleClose();
    }, [taskModal, addTask, editTask, setCurrentActiveInspectionTask]);

    // Task Link modal submit handler - no need to close here, the modal handles its own close
    const handleTaskLinkSubmit = useCallback(async (data: TaskLinkFormData) => {
        if (taskLinkModal.itemToEdit) {
            await editTaskLink(taskLinkModal.itemToEdit.itLinkId, data);
        } else {
            await addTaskLink(data);
        }
        taskLinkModal.handleClose();
    }, [taskLinkModal, addTaskLink, editTaskLink]);

    // Memoized initial data for modals to prevent unnecessary re-renders
    const taskInitialData = useMemo(() =>
        taskModal.itemToEdit ? {
            taskHeader: taskModal.itemToEdit.taskHeader,
            taskDescription: taskModal.itemToEdit.taskDescription,
            order: taskModal.itemToEdit.listOrder
        } : null,
        [taskModal.itemToEdit]
    );

    const taskLinkInitialData = useMemo(() =>
        taskLinkModal.itemToEdit ? {
            linkUrl: taskLinkModal.itemToEdit.linkUrl,
            linkText: taskLinkModal.itemToEdit.linkText,
            linkCategory: taskLinkModal.itemToEdit.linkCategory
        } : null,
        [taskLinkModal.itemToEdit]
    );

    const renderAddTaskButton = useCallback(() => (
        <div className="p-4 mt-12 border-t flex justify-center">
            <button
                data-testid="add-task-button"
                onClick={taskModal.handleOpenForAdd}
                disabled={!currentActiveModel}
                className={primaryButtonClass}
            >
                Add Task
            </button>
        </div>
    ), [currentActiveModel, taskModal.handleOpenForAdd]);

    const renderAddTaskLinkButton = useCallback(() => (
        <div className="p-4 mt-12 border-t flex justify-center">
            <button
                data-testid="add-task-link-button"
                onClick={taskLinkModal.handleOpenForAdd}
                disabled={!activeInspectionTaskId}
                className={primaryButtonClass}
            >
                Add Link
            </button>
        </div>
    ), [activeInspectionTaskId, taskLinkModal.handleOpenForAdd]);

    if (isLoading) {
        return <Loader loaders={{ isInspectionsLoading: true }} />;
    }

    return (
        <div data-testid="inspections-details-page" className="flex h-screen">
            <ChecklistSidebar
                models={injectedModelData}
                current={currentActiveModel}
                onSelect={onSelectModel}
                onClickAddInspectionToModel={onClickAddInspectionToModel}
            />

            <div className="flex-1 flex flex-col p-[.5rem]">
                <div className="flex overflow-auto gap-2">
                    <div data-testid="inspection-tasks-panel" className="flex-1 border-r shadow-lg">
                        <InspectionPaginatedTable
                            tableProps={inspectionTaskTableData}
                            pagination={inspectionPagination}
                            onSearch={setInspectionSearchTerm}
                            renderChild={renderAddTaskButton}
                        />
                    </div>
                    <div data-testid="inspection-task-links-panel" className="w-2/5">
                        <InspectionPaginatedTable
                            tableProps={inspectionTaskLinksData}
                            pagination={inspectionTaskLinksPagination}
                            onSearch={setLinkSearchTerm}
                            renderChild={renderAddTaskLinkButton}
                        />
                    </div>
                </div>
            </div>

            {taskModal.isOpen && <AddTaskModal
                isOpen={taskModal.isOpen}
                onClose={taskModal.handleClose}
                onSubmit={handleTaskSubmit}
                initialData={taskInitialData}
                mode={taskModal.isEditMode ? 'edit' : 'add'}
            />}

            {taskLinkModal.isOpen && <AddTaskLinkModal
                isOpen={taskLinkModal.isOpen}
                onClose={taskLinkModal.handleClose}
                onSubmit={handleTaskLinkSubmit}
                initialData={taskLinkInitialData}
                mode={taskLinkModal.isEditMode ? 'edit' : 'add'}
            />}

        </div>
    );
}